import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Document, FlatDocumentSection } from 'services/DocumentService'
import { DOCUMENTS_FAVOURITING } from 'constants/feature-flags'
import { AppState } from 'redux/store'
import Header from './Header'
import Nav from './Nav'
import Section from './Section'

interface SectionDetailsProps {
  data: (FlatDocumentSection & { sections: Array<FlatDocumentSection & { documents: Document[] }> })
  aol: string
  sectionId: string
  focusedId: string | null
  pageId: string
  documentFavourites?: string[]
}

export default function SectionDetails (props: SectionDetailsProps): JSX.Element {
  const selectedDocuments = useSelector((state: AppState) => state.documents.selectedDocuments)
  const showAutomatedOnly = useSelector((state: AppState) => state.documents.showAutomatedOnly)
  const showFavouritesOnly = useSelector((state: AppState) => state.documents.showFavouritesOnly)
  const { documentFavourites = [] } = props

  const favouritesSet = useMemo(() => new Set(documentFavourites), [documentFavourites])

  const hasAutomatedDocuments = useMemo(
    () => props.data.sections.some(section => section.documents.some(doc => doc.automated)),
    [props.data.sections]
  )

  const filteredSections = useMemo(() => {
    if (!DOCUMENTS_FAVOURITING || showFavouritesOnly !== true) return props.data.sections
    return props.data.sections.reduce<Array<FlatDocumentSection & { documents: Document[] }>>((acc, section) => {
      const documents = section.documents.filter(doc => favouritesSet.has(doc.id))
      if (documents.length === 0) return acc
      return [...acc, { ...section, documents }]
    }, [])
  }, [props.data.sections, showFavouritesOnly, favouritesSet])

  return (
    <div>
      <Nav title={props.data.title} hasAutomatedDocuments={hasAutomatedDocuments} />
      <div className='layout-width-constraint'>
        <Header
          title={props.data.title}
          selectedDocuments={selectedDocuments}
          sectionId={props.sectionId}
        />
        <div className='mt6'>
          {DOCUMENTS_FAVOURITING && showFavouritesOnly === true && filteredSections.length === 0
            ? (
              <p className='f2 tc i'>
                {showAutomatedOnly === true ? 'No automated favourites selected' : 'No favourites selected'}
              </p>
              )
            : (
                filteredSections.map((section, index) => (
                  <Section
                    rootId={props.sectionId}
                    key={`${section.id}-${section.areaOfLaw}-${index}`}
                    aol={props.aol}
                    sectionId={section.id}
                    title={section.title}
                    focusedId={props.focusedId}
                    documents={section.documents}
                    selectedDocuments={selectedDocuments}
                    pageId={props.pageId}
                    favourites={favouritesSet}
                  />
                ))
              )}
        </div>
      </div>
    </div>
  )
}
